/**
 * Blog index page — lists all published posts in English (primary) with a
 * sub-list for Spanish. SEO-optimized: server-rendered <title> and meta tags
 * for the blog hub, plus JSON-LD Blog markup. Posts are loaded from the
 * static manifest so the index renders instantly.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { POSTS } from '@/content/blogManifest';
import DirectInstallButton from '@/components/DirectInstallButton';
import { BookOpen, Search, ArrowRight, Globe2 } from 'lucide-react';

function setSeo({ title, description, canonical, jsonLd }) {
  if (typeof document === 'undefined') return;
  document.title = title;
  const setMeta = (selector, attrs) => {
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement('meta');
      Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
    }
    Object.entries(attrs.set || {}).forEach(([k, v]) => el.setAttribute(k, v));
  };
  setMeta('meta[name="description"]', { set: { content: description } });
  setMeta('meta[property="og:title"]', { create: { property: 'og:title' }, set: { content: title } });
  setMeta('meta[property="og:description"]', { create: { property: 'og:description' }, set: { content: description } });
  setMeta('meta[property="og:url"]', { create: { property: 'og:url' }, set: { content: canonical } });

  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) { link = document.createElement('link'); link.rel = 'canonical'; document.head.appendChild(link); }
  link.href = canonical;

  let ld = document.getElementById('pm-blog-jsonld');
  if (!ld) { ld = document.createElement('script'); ld.type = 'application/ld+json'; ld.id = 'pm-blog-jsonld'; document.head.appendChild(ld); }
  ld.textContent = JSON.stringify(jsonLd);
}

export default function Blog() {
  const [lang, setLang] = useState('en');
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    let list = POSTS.filter(p => p.lang === lang);
    if (k) list = list.filter(p =>
      p.title.toLowerCase().includes(k) ||
      p.description.toLowerCase().includes(k) ||
      (p.keywords || '').toLowerCase().includes(k)
    );
    return list;
  }, [lang, q]);

  useEffect(() => {
    const isEs = lang === 'es';
    setSeo({
      title: isEs
        ? 'Blog de Basketball — Playbook #1 | PlayMaster IA'
        : 'Basketball Coaching Blog — Playbook #1 | PlayMaster IA',
      description: isEs
        ? 'Guías, tácticas y jugadas de basketball: playbook, motion, press break, jugadas juveniles y la mejor app para entrenadores.'
        : 'Basketball coaching guides, tactics and plays: playbook, motion offense, press break, youth plays and the best basketball coaching app.',
      canonical: `https://playmasteria.com/blog${isEs ? '?lang=es' : ''}`,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: isEs ? 'Blog de Basketball — PlayMaster IA' : 'PlayMaster IA — Basketball Coaching Blog',
        url: `https://playmasteria.com/blog`,
        inLanguage: isEs ? 'es' : 'en',
        publisher: {
          '@type': 'Organization',
          name: 'PlayMaster IA',
          url: 'https://playmasteria.com/',
        },
        blogPost: POSTS.filter(p => p.lang === lang).map(p => ({
          '@type': 'BlogPosting',
          headline: p.title,
          description: p.description,
          datePublished: p.date,
          url: `https://playmasteria.com/blog/${p.slug}`,
        })),
      },
    });
  }, [lang]);

  return (
    <main className="min-h-screen bg-navy-deep text-white px-4 sm:px-8 py-8" data-testid="blog-index">
      {/* Top bar */}
      <header className="max-w-5xl mx-auto mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <Link to="/" className="inline-flex items-center gap-2 text-gold/80 hover:text-gold mb-4 text-xs font-mono uppercase tracking-[0.3em]">
            <BookOpen className="w-4 h-4" /> PlayMaster IA
          </Link>
          <h1 className="font-h uppercase text-4xl sm:text-5xl text-white">
            {lang === 'es' ? 'Blog de Basketball' : 'Basketball Coaching Blog'}
          </h1>
          <p className="text-gray-400 text-sm mt-1 max-w-xl">
            {lang === 'es'
              ? 'Guías, tácticas y jugadas — escritas por coaches, optimizadas para vos.'
              : 'Coaching guides, tactics and plays — written by coaches, optimized for you.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DirectInstallButton variant="primary" lang={lang} />
        </div>
      </header>

      {/* Language switch + search */}
      <div className="max-w-5xl mx-auto mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="blog-filters">
        <div className="inline-flex rounded-full border-2 border-gold/30 overflow-hidden" data-testid="blog-lang-switch">
          <button
            onClick={() => setLang('en')}
            data-testid="blog-lang-en"
            className={`px-4 py-2 text-xs font-h uppercase tracking-wider ${lang === 'en' ? 'bg-gold text-navy font-semibold' : 'text-gold/70 hover:text-gold'}`}
          >
            <Globe2 className="w-3.5 h-3.5 inline me-1" /> English
          </button>
          <button
            onClick={() => setLang('es')}
            data-testid="blog-lang-es"
            className={`px-4 py-2 text-xs font-h uppercase tracking-wider ${lang === 'es' ? 'bg-gold text-navy font-semibold' : 'text-gold/70 hover:text-gold'}`}
          >
            <Globe2 className="w-3.5 h-3.5 inline me-1" /> Español
          </button>
        </div>
        <div className="relative w-full sm:w-auto sm:min-w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gold/60" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={lang === 'es' ? 'Buscar en el blog…' : 'Search the blog…'}
            data-testid="blog-search"
            className="w-full bg-card-steel border border-gold/30 focus:border-gold rounded-md pl-10 pr-3 py-2 text-sm text-white outline-none"
          />
        </div>
      </div>

      {/* Posts grid */}
      <section className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="blog-grid">
        {filtered.map((p) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            data-testid={`blog-card-${p.slug}`}
            className="block bg-card-steel border border-gold/30 hover:border-gold rounded-lg p-5 transition group"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70 mb-2">
              {p.lang === 'es' ? 'Español' : 'English'} · {p.date}
            </div>
            <h2 className="font-h text-xl text-white group-hover:text-gold transition mb-2">{p.title}</h2>
            <p className="text-sm text-gray-300 line-clamp-3">{p.description}</p>
            <div className="mt-4 inline-flex items-center gap-1 text-gold text-xs font-h uppercase tracking-wider">
              {p.lang === 'es' ? 'Leer artículo' : 'Read article'} <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-gray-500 py-10">
            {lang === 'es' ? `Sin resultados para "${q}".` : `No results for "${q}".`}
          </div>
        )}
      </section>

      {/* Footer CTA */}
      <footer className="max-w-5xl mx-auto mt-16 p-6 bg-gradient-to-r from-gold/10 to-card-steel border-2 border-gold/40 rounded-xl text-center" data-testid="blog-footer-cta">
        <h3 className="font-h uppercase text-2xl mb-2">
          {lang === 'es' ? '¿Listo para crear tu primer playbook?' : 'Ready to build your first playbook?'}
        </h3>
        <p className="text-sm text-gray-300 mb-4 max-w-xl mx-auto">
          {lang === 'es'
            ? 'Basketball Playbook #1 — la app más fácil, rápida e inteligente. Instalación directa, sin pasos extra.'
            : 'Basketball Playbook #1 — the easiest, fastest and smartest. Direct install, no extra steps.'}
        </p>
        <DirectInstallButton variant="primary" lang={lang} />
      </footer>
    </main>
  );
}
